import * as React from 'react';
import {
  MasonryEngineConfigContext,
  useMasonryEngineActionEmitter,
  type MasonryEngineStateController,
} from '@leagueplatform/masonry-engine';
import type { StoreApi } from 'zustand/vanilla';
import {
  selectChatSession,
  useSelectedSessionId,
} from '../../chat-selection-store';
import type { SelectedChat } from './paginated-chat-session-list-renderer.component';

type StateControllerStore = StoreApi<MasonryEngineStateController>;

export type SelectionItem = {
  /** Masonry node id — used to dispatch per-item state. */
  nodeId: string;
  /** Stable chat-session id — used to match selection across reloads. */
  sessionId: string;
};

type UseItemSelectionOptions = {
  items: ReadonlyArray<SelectionItem>;
  selectedChat: SelectedChat | undefined;
  currentPage: number;
  nodeId: string;
  paginationStrategy?: 'page' | 'cursor';
};

export type UseItemSelectionResult = {
  handleItemClick: (index: number) => void;
};

type ItemNodeState = { isSelected: boolean };
type ListNodeState = { hasActiveChat: boolean; selectedChat?: SelectedChat };
type DispatchableState = ItemNodeState | ListNodeState;

const NODE_INIT_WARN_MS = 5000;

/**
 * Runs `onReady` once every `nodeIds` entry has state registered in the
 * masonry store. Masonry throws on `setNodeState` for uninitialized
 * nodes, so we gate the mirror dispatch on initialization.
 *
 * Returns a disposer that cancels any pending subscription.
 */
const runWhenNodesInitialized = (
  store: StateControllerStore | undefined,
  nodeIds: ReadonlyArray<string>,
  onReady: () => void,
): (() => void) => {
  if (!store) {
    onReady();
    return () => {};
  }

  const allReady = () =>
    nodeIds.every((id) => store.getState().nodeStateMap.has(id));

  if (allReady()) {
    onReady();
    return () => {};
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = store.subscribe(() => {
    if (!allReady()) return;
    unsubscribe();
    if (timeoutId) clearTimeout(timeoutId);
    onReady();
  });

  // Dev-only escape hatch: warn if nodes never register (schema issue
  // or upstream failure) instead of holding the subscription silently.
  if (process.env.NODE_ENV !== 'production') {
    timeoutId = setTimeout(() => {
      if (!allReady()) {
        // eslint-disable-next-line no-console
        console.warn(
          '[useItemSelection] Timed out waiting for item-node state initialization; selection mirror skipped.',
        );
      }
    }, NODE_INIT_WARN_MS);
  }

  return () => {
    unsubscribe();
    if (timeoutId) clearTimeout(timeoutId);
  };
};

/**
 * Persists chat-session selection across masonry reloads (pagination).
 *
 * Selection lives in a module-level zustand store (`chat-selection-store`),
 * outside masonry — masonry replaces node state wholesale on async
 * refresh, so anything attached there would be clobbered on each page
 * change. Items read the store directly.
 *
 * Matches by `sessionId` (stable per schema), not node `id` (not stable
 * across fetches).
 *
 * Selection is also mirrored back into masonry node state after each
 * reload for store consumers (e.g. layout's `hasActiveChat`); visual
 * selection does not depend on those dispatches landing.
 */
export const useItemSelection = ({
  items,
  selectedChat,
  currentPage,
  nodeId,
  paginationStrategy,
}: UseItemSelectionOptions): UseItemSelectionResult => {
  const invokeAction = useMasonryEngineActionEmitter();
  const masonryConfig = React.useContext(MasonryEngineConfigContext);
  const isCursorMode = paginationStrategy === 'cursor';

  const selectedSessionId = useSelectedSessionId();
  const hasSeededRef = React.useRef(false);
  // Reload detection depends on `items` being memoized upstream (the
  // renderer memos over `nodeData.sections?.items`). Drop that memo and
  // this hook stops detecting reloads.
  const prevItemsRef = React.useRef<ReadonlyArray<SelectionItem>>(items);
  const isInitialMountRef = React.useRef(true);

  const setMasonryNodeState = React.useCallback(
    (id: string, state: DispatchableState) => {
      invokeAction({
        type: 'setNodeState',
        namespace: 'default',
        payload: { nodeId: id, state },
      });
    },
    [invokeAction],
  );

  // Seed once from the prop so a backend-persisted pre-selection lands
  // before any click. After that, clicks are authoritative.
  React.useEffect(() => {
    if (hasSeededRef.current) return;
    // Cursor mode has no page numbers — seeding by `{page, index}` would
    // risk highlighting the wrong item.
    if (isCursorMode) return;
    if (
      !selectedChat ||
      selectedChat.page !== currentPage ||
      selectedChat.index >= items.length
    ) {
      return;
    }
    const item = items[selectedChat.index];
    if (!item) return;
    hasSeededRef.current = true;
    selectChatSession(item.sessionId);
  }, [selectedChat, currentPage, items, isCursorMode]);

  // Mirror selection into masonry state on reload. Skipped on mount:
  // the item click flow loads the chat content, so flipping
  // `hasActiveChat` without a click would surface an error state in
  // the chat section.
  React.useLayoutEffect(() => {
    const itemsChanged = prevItemsRef.current !== items;
    prevItemsRef.current = items;

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return undefined;
    }
    if (!itemsChanged || !selectedSessionId || items.length === 0) {
      return undefined;
    }

    const targetIndex = items.findIndex(
      (item) => item.sessionId === selectedSessionId,
    );

    const mirror = () => {
      items.forEach((item, i) => {
        setMasonryNodeState(item.nodeId, { isSelected: i === targetIndex });
      });
      if (targetIndex === -1) {
        // Selected session not on this page (deleted / reordered off).
        // Intentionally clear: the captured index would point at the
        // wrong chat. The chat panel is a separate node, still correct.
        return;
      }
      // Cursor mode has no meaningful `{page, index}`; only mirror
      // `hasActiveChat`.
      if (isCursorMode) {
        setMasonryNodeState(nodeId, { hasActiveChat: true });
        return;
      }
      // Correct the masonry list-node state whenever it diverges from
      // the selection store — the backend can rehydrate `selectedChat`
      // with a stale default after the user picked something else.
      const masonryIsStale =
        !selectedChat ||
        selectedChat.page !== currentPage ||
        selectedChat.index !== targetIndex;
      if (masonryIsStale) {
        setMasonryNodeState(nodeId, {
          hasActiveChat: true,
          selectedChat: { page: currentPage, index: targetIndex },
        });
      }
    };

    return runWhenNodesInitialized(
      masonryConfig?.stateControllerStore,
      items.map((item) => item.nodeId),
      mirror,
    );
  }, [
    items,
    selectedChat,
    currentPage,
    nodeId,
    selectedSessionId,
    setMasonryNodeState,
    masonryConfig,
    isCursorMode,
  ]);

  const handleItemClick = React.useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;

      hasSeededRef.current = true;
      selectChatSession(item.sessionId);

      items.forEach((it, i) => {
        setMasonryNodeState(it.nodeId, { isSelected: i === index });
      });
      // Cursor mode has no meaningful `{page, index}` — writing one
      // would pin a stale coordinate for the next reload, and the
      // mirror effect deliberately omits it for the same reason.
      if (isCursorMode) {
        setMasonryNodeState(nodeId, { hasActiveChat: true });
        return;
      }
      setMasonryNodeState(nodeId, {
        hasActiveChat: true,
        selectedChat: { page: currentPage, index },
      });
    },
    [items, nodeId, currentPage, setMasonryNodeState, isCursorMode],
  );

  return { handleItemClick };
};
